import { ComparisonOperatorDTOKeywordEnum, type ConceptDefinitionDTO } from "@eu.mdotm.api/smart-workspaces";
import type { ReactNode } from "react";
import { type ComparisonChoice, comparisonChoiceOf, isPresenceComparison } from "../comparison-operator";
import { type SelectConceptConditionProps, workspaceDataTypes } from "../data-type";
import { ChainableElement } from "./ChainableElement";
import { ConceptChain, type ConceptChainTail, filterStepOrder } from "./ConceptChain";
import type { WorkspaceChain, WorkspaceChainPredicate } from "./type";

export type FilterWorkspaceChain = WorkspaceChain & WorkspaceChainPredicate;

/**
 * The chain a filter is built with: the concept's own parameterization, plus a condition.
 *
 * Everything above the condition is {@link ConceptChain} — the same steps the chart and column panels
 * show, in the order this panel shows them ({@link filterStepOrder}). What is the filter's own is the
 * tail: which operator to offer and what control takes its value.
 */
export function WorkspaceFilterChain(props: {
	initialValue?: FilterWorkspaceChain;
	onChange(newWorkspaceChain: FilterWorkspaceChain): void;
	autogenerateName: boolean;
}): ReactNode[] {
	return ConceptChain<FilterWorkspaceChain>({
		initialValue: props.initialValue,
		onChange: props.onChange,
		autogenerateName: props.autogenerateName,
		stepOrder: filterStepOrder,
		tail: filterConditionTail,
	});
}

/**
 * The operator a freshly picked concept starts on: the first one it declares.
 *
 * Exported because the chain is not the only place a filter is seeded — the table's "Create filter" quick
 * action builds one from a column without opening the panel, and a default that disagreed with the panel's
 * would show the user a different operator depending on where the filter came from.
 */
export function defaultComparisonMode(
	concept: ConceptDefinitionDTO | undefined,
): ComparisonOperatorDTOKeywordEnum | undefined {
	return concept?.allowedComparisonOperators?.at(0);
}

/**
 * The filter's tail: one condition step, or none.
 *
 * Which control the value is taken in, whether the `in` operator offers a multi-select, whether the
 * control needs the concept's id to fetch its options, and whether switching operator keeps the value
 * already entered are all questions about the concept's `dataType`, so all four are answered by that
 * type's row in `workspaceDataTypes` rather than by an `if` per type here. A type whose row has no
 * `condition` — `TIME`, `TAGS` and `TIME_SERIES` — is a type no filter can be built on, and the chain
 * simply stops after the parameter steps.
 */
const filterConditionTail: ConceptChainTail<FilterWorkspaceChain> = {
	seed: (concept) => ({ comparisonMode: defaultComparisonMode(concept) }),
	steps: ({ chain, concept, update }) => {
		// A concept the catalogue published without a data type answers no question about how its value is
		// compared, so there is nothing to build a condition out of.
		const dataType = concept.dataType;
		const spec = dataType ? workspaceDataTypes[dataType].condition : null;
		if (!spec) {
			return [];
		}
		// A `SPHERE_ENTITY` condition is a pick from a list of entities, and only the five concepts that
		// name an entity have one to publish — for any other, there would be nothing to choose from.
		if (spec.availableFor && !spec.availableFor.includes(concept.id!)) {
			return [];
		}

		const conditionProps = {
			conditionType: dataType,
			allowedComparison: concept.allowedComparisonOperators ?? [],
			comparisonMode: chain.comparisonMode ?? null,
			// The *choice*, not the keyword: `exist` publishes one keyword and offers two entries, so which of
			// them a reopened filter is on can only be read back out of its argument.
			comparisonChoice: comparisonChoiceOf(chain.comparisonMode, chain.comparisonValue),
			initialComparisonValue: comparisonValueForOperator(chain.comparisonMode, chain.comparisonValue),
			...(spec.multiSelectOnIn && chain.comparisonMode === ComparisonOperatorDTOKeywordEnum.In ? { multi: true } : {}),
			...(spec.needsConceptId ? { conceptId: concept.id } : {}),
			...(spec.showsUnit ? { comparisonUnit: concept.unitOfMeasurement } : {}),
			onChangeComparisonMode(newChoice: ComparisonChoice | null) {
				update((latest) => ({
					...latest,
					comparisonMode: newChoice?.keyword,
					comparisonValue: comparisonValueForChoice(
						newChoice,
						spec.keepsValueOnOperatorChange ? latest.comparisonValue : null,
					),
				}));
			},
			onChangeConceptComparisonValue(newComparisonValue: { data: unknown }) {
				update((latest) => ({
					...latest,
					comparisonValue: newComparisonValue.data as FilterWorkspaceChain["comparisonValue"],
				}));
			},
		};

		return [
			// Keyed on the data type, so that switching to a concept whose value is taken differently
			// remounts the control instead of leaving the previous concept's entry on screen.
			<ChainableElement.SelectConceptCondition
				key={`step-condition-${dataType}`}
				// One cast, for the same reason `SelectConceptConditionValue` needs one: the props are a
				// thirteen-way union correlated on `conditionType`, and the object above is the one shape every
				// member of it has. What varies between members is exactly what the spec above supplies.
				{...(conditionProps as unknown as SelectConceptConditionProps)}
			/>,
		];
	},
};

/**
 * The comparison value a chosen operator starts on.
 *
 * A presence choice supplies its own: the argument *is* the choice, so it is written rather than carried
 * over — `keepsValueOnOperatorChange` is about carrying a value between operators that take one from the
 * user. Every other operator re-shapes whatever survives, below.
 */
function comparisonValueForChoice(choice: ComparisonChoice | null, comparisonValue: any): any {
	if (choice && isPresenceComparison(choice.keyword)) {
		return [choice.presence];
	}
	return comparisonValueForOperator(choice?.keyword, comparisonValue);
}

/**
 * The comparison value, reshaped for the operator that is about to be applied to it.
 *
 * An operator decides how many values it takes and whether each is a list: `in` takes one list, `btw`
 * takes two scalars, everything else takes one scalar. The stored value is always a tuple, so switching
 * operator means re-tupling whatever survives — and dropping it when the shape cannot be reconciled.
 */
function comparisonValueForOperator /* <T extends string | number | boolean | string[]> */(
	comparisonMode: WorkspaceChainPredicate["comparisonMode"] | undefined | null,
	comparisonValue: any,
): /* [T[]] | [T|null]|[T | null, T | null] */ any {
	// this typing is way to weird, refactor it please
	if (comparisonMode === ComparisonOperatorDTOKeywordEnum.In) {
		if (Array.isArray(comparisonValue) && Array.isArray(comparisonValue[0])) {
			return [comparisonValue[0]];
		}
		return [[]];
	}
	if (comparisonMode === ComparisonOperatorDTOKeywordEnum.Btw) {
		if (Array.isArray(comparisonValue) && !Array.isArray(comparisonValue[0]) && !Array.isArray(comparisonValue[1])) {
			return [comparisonValue[0], comparisonValue[1]];
		}
		return [null, null];
	}

	if (comparisonMode === ComparisonOperatorDTOKeywordEnum.Contains) {
		if (Array.isArray(comparisonValue)) {
			return [comparisonValue.join("")];
		}
		return [null, null];
	}

	if (Array.isArray(comparisonValue)) {
		return [comparisonValue[0] ?? null];
	}

	return [null];
}
